import { FC, ReactNode, useRef, useState } from 'react';

import classNames from 'classnames';
import { RemoveScrollBar } from 'react-remove-scroll-bar';

import { TeliaThemeProvider } from '@telialt/common-theme/src/TeliaThemeProvider';
import { DataTest } from '@telialt/utils/src/helpers';

import {
    ModalBody,
    ModalBodyBottomShadow,
    ModalBodyContent,
    ModalBodyScrollable,
    ModalBodyTopShadow,
    ModalContent,
    ModalOverlay,
    StyledModal,
} from './Modal.styles';
import { ModalSizeType } from './model';
import { MODAL_TEST_ID } from './test-ids';

export type Types = 'wide' | 'fullscreen' | 'mobile-not-full' | 'light-grey';

export type Props = {
    isShown?: boolean;
    header?: ReactNode;
    footer?: ReactNode;
    size?: ModalSizeType;
    className?: string;
    modalClassName?: string;
    bodyClassName?: string;
    bodyContentClassName?: string;
    fixedDesktopHeight?: string;
    isScrollable?: boolean;
    type?: Types | Types[];
    onHide?: () => void;
    children?: ReactNode;
    loaderContent?: ReactNode;
} & DataTest;

const Modal: FC<Props> = ({
    isShown,
    onHide,
    header,
    footer,
    size,
    className,
    modalClassName,
    bodyClassName,
    bodyContentClassName = classNames('with-side-spacing', !footer && 'without-footer'),
    fixedDesktopHeight,
    isScrollable = true,
    type,
    children,
    dataTest,
    loaderContent,
}) => {
    const [isTopShadowVisible, setIsTopShadowVisible] = useState(false);
    const [isBottomShadowVisible, setIsBottomShadowVisible] = useState(false);
    const modalBodyObserverRef = useRef<IntersectionObserver>(null);
    const scrollableBeginRef = useRef<HTMLDivElement>(null);
    const scrollableEndRef = useRef<HTMLDivElement>(null);

    const typeClassList = typeof type === 'string' ? `modal-${type}` : type?.map((item) => `modal-${item}`);
    const classList = classNames(typeClassList, className);

    const bodyContentClassList = classNames(bodyContentClassName, 'modal-body');

    const handleModalOpen = () => {
        if (!window.IntersectionObserver || isScrollable === false) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting }) => {
                if (target === scrollableBeginRef.current) {
                    setIsTopShadowVisible(!isIntersecting);
                } else if (target === scrollableEndRef.current) {
                    setIsBottomShadowVisible(!isIntersecting);
                }
            });
        });

        if (scrollableBeginRef?.current) {
            observer.observe(scrollableBeginRef.current);
        }

        if (scrollableEndRef?.current) {
            observer.observe(scrollableEndRef.current);
        }

        modalBodyObserverRef.current = observer;
    };

    const handleModalClose = () => {
        if (!window.IntersectionObserver || isScrollable === false) {
            return;
        }
        modalBodyObserverRef.current?.disconnect();
    };

    return (
        <TeliaThemeProvider>
            <StyledModal
                ariaHideApp={false}
                className={classList}
                bodyOpenClassName=""
                overlayClassName="overlay"
                overlayElement={(overlayProps, contentElement) => (
                    <ModalOverlay {...overlayProps} data-test={MODAL_TEST_ID.OVERLAY}>
                        {contentElement}
                    </ModalOverlay>
                )}
                isOpen={!!isShown}
                onRequestClose={onHide}
                onAfterOpen={handleModalOpen}
                onAfterClose={handleModalClose}
                // Not sure why types are failing
                // @ts-ignore-next-line
                $size={size}
                data={{ test: dataTest || MODAL_TEST_ID.DIALOG }}
                parentSelector={() =>
                    // Changing react portal container in storybook, so it would not leak into other pages
                    (process.env.NODE_ENV === 'development' && document.querySelector('.sb-show-main #root')) ||
                    document.body
                }
            >
                <RemoveScrollBar gapMode="padding" />
                <ModalContent data-test={MODAL_TEST_ID.CONTENT} className={modalClassName}>
                    {header && <div data-test={MODAL_TEST_ID.HEADER}>{header}</div>}
                    <ModalBody className={bodyClassName} data-test={MODAL_TEST_ID.BODY}>
                        {isScrollable ? (
                            <>
                                <ModalBodyTopShadow $isVisible={isTopShadowVisible} />
                                <ModalBodyScrollable>
                                    <div ref={scrollableBeginRef} />
                                    <ModalBodyContent
                                        className={bodyContentClassList}
                                        $height={fixedDesktopHeight}
                                        data-test={MODAL_TEST_ID.BODY_CONTENT}
                                    >
                                        {children}
                                    </ModalBodyContent>
                                    <div ref={scrollableEndRef} />
                                </ModalBodyScrollable>
                                <ModalBodyBottomShadow $isVisible={isBottomShadowVisible} />
                            </>
                        ) : (
                            <ModalBodyContent className={bodyContentClassList} $height={fixedDesktopHeight}>
                                {children}
                            </ModalBodyContent>
                        )}
                    </ModalBody>
                    {footer && <div data-test={MODAL_TEST_ID.FOOTER}>{footer}</div>}
                    {loaderContent}
                </ModalContent>
            </StyledModal>
        </TeliaThemeProvider>
    );
};
export default Modal;
